import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Upload, Camera, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ExpenseFormFields, fromExpense, toPayload } from "./ExpenseFormDialog";
import { FilePreview } from "./FilePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReceiptImage = ({ receiptId, contentType, className = "" }) => (
  <FilePreview url={`${API}/receipts/${receiptId}/file`} contentType={contentType} className={className} />
);

export const ReceiptReviewPanel = ({ result, categories, vendors, customers, onVendorCreated, onDone }) => {
  const [form, setForm] = useState(fromExpense(result.expense));
  const [busy, setBusy] = useState(false);
  const ex = result.extraction, ai = result.expense.ai;
  const suggested = categories.find((c) => c.key === ai?.suggested_category_key);
  const save = async (confirm) => {
    setBusy(true);
    try {
      const body = toPayload({ ...form, status: confirm ? "confirmed" : "needs_review", receipt_id: result.receipt.id });
      const { data } = await axios.put(`${API}/expenses/${result.expense.id}`, body);
      toast.success(confirm ? "Expense confirmed" : "Saved as draft");
      onDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const conf = Math.round((ai?.confidence || 0) * 100);
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="receipt-review-panel">
      <div className="space-y-2">
        <ReceiptImage receiptId={result.receipt.id} contentType={result.receipt.content_type} />
        <p className="text-xs text-slate-500 truncate">{result.receipt.filename} · {(result.receipt.size_bytes / 1024).toFixed(0)} KB</p>
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          {ex ? <Badge className={conf >= 80 ? "bg-emerald-100 text-emerald-800" : conf >= 50 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"} data-testid="receipt-confidence">AI confidence {conf}%</Badge>
              : <Badge className="bg-slate-100 text-slate-700" data-testid="receipt-ai-unavailable">{result.receipt.ocr_status === "failed" ? `AI failed: ${result.receipt.ocr_error || "error"}` : "AI extraction off — enter manually"}</Badge>}
          {suggested && <Badge variant="outline" className="border-indigo-300 text-indigo-700" data-testid="receipt-ai-suggestion">AI suggested: {suggested.name}{ai.applied_rule !== "ai" ? ` · applied ${ai.applied_rule.replace("_", " ")}` : ""}</Badge>}
        </div>
        {result.duplicate_warning && <div className="flex items-start gap-2 p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="receipt-duplicate-warning"><AlertTriangle className="w-4 h-4 mt-0.5" /> Possible duplicate: {result.duplicate_warning.reason}{result.duplicate_warning.date ? ` (${result.duplicate_warning.date}, $${result.duplicate_warning.total})` : ""}. Review before confirming.</div>}
        <ExpenseFormFields form={form} setForm={setForm} categories={categories} vendors={vendors} customers={customers} onVendorCreated={onVendorCreated} testPrefix="review" />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="receipt-review-save-draft">Save as draft</Button>
          <Button disabled={busy || !form.category_key} onClick={() => save(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="receipt-review-confirm"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm</Button>
        </div>
      </div>
    </div>
  );
};

export const ReceiptUploadDialog = ({ open, onOpenChange, categories, vendors, customers, onVendorCreated, onExpenseSaved }) => {
  const [queue, setQueue] = useState([]);
  const [review, setReview] = useState(null);
  const [drag, setDrag] = useState(false);

  const upload = async (files) => {
    const items = Array.from(files).map((f) => ({ id: Math.random().toString(36).slice(2), name: f.name, progress: 0, status: "uploading", file: f }));
    setQueue((q) => [...q, ...items]);
    for (const it of items) {
      const fd = new FormData(); fd.append("file", it.file); fd.append("auto_extract", "true");
      try {
        const { data } = await axios.post(`${API}/receipts/upload`, fd, { onUploadProgress: (p) => setQueue((q) => q.map((x) => x.id === it.id ? { ...x, progress: Math.round((p.loaded / (p.total || 1)) * 90) } : x)) });
        setQueue((q) => q.map((x) => x.id === it.id ? { ...x, progress: 100, status: "done", result: data } : x));
        onExpenseSaved(data.expense, true);
        if (!review) setReview(data);
      } catch (e) {
        setQueue((q) => q.map((x) => x.id === it.id ? { ...x, status: "error", error: e.response?.data?.detail || "Upload failed" } : x));
        toast.error(`${it.name}: ${e.response?.data?.detail || "Upload failed"}`);
      }
    }
  };
  const pending = queue.filter((q) => q.status === "done" && q.result && (!review || q.result.receipt.id !== review.receipt.id));
  const close = (o) => { if (!o) { setQueue([]); setReview(null); } onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[980px] max-h-[92vh] overflow-y-auto" data-testid="receipt-upload-dialog">
        <DialogHeader><DialogTitle>Scan receipts</DialogTitle></DialogHeader>
        {!review && (
          <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400"}`} data-testid="receipt-dropzone">
            <Camera className="w-8 h-8 text-emerald-600" />
            <p className="font-medium text-slate-800">Tap to take a photo or choose files</p>
            <p className="text-xs text-slate-500">JPG, PNG, WEBP, HEIC or PDF · up to 10 MB each · drag &amp; drop on desktop</p>
            <input type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="receipt-file-input" />
          </label>
        )}
        {queue.length > 0 && (
          <div className="space-y-2" data-testid="receipt-queue">
            {queue.map((q) => (
              <div key={q.id} className="flex items-center gap-3 text-sm" data-testid="receipt-queue-item">
                <Upload className="w-4 h-4 text-slate-400" /><span className="flex-1 truncate">{q.name}</span>
                {q.status === "error" ? <span className="text-red-600">{q.error}</span> : <Progress value={q.progress} className="w-32 h-2" />}
                {q.status === "done" && q.result && (!review || review.receipt.id !== q.result.receipt.id) && <Button size="sm" variant="outline" onClick={() => setReview(q.result)} data-testid="receipt-queue-review">Review</Button>}
              </div>
            ))}
          </div>
        )}
        {review && <ReceiptReviewPanel key={review.receipt.id} result={review} categories={categories} vendors={vendors} customers={customers} onVendorCreated={onVendorCreated}
          onDone={(saved) => { onExpenseSaved(saved); const next = pending[0]; setReview(next ? next.result : null); if (!next && queue.every((q) => q.status !== "uploading")) close(false); }} />}
      </DialogContent>
    </Dialog>
  );
};
